/**
 * ENG-9483 slice 2 (epic ENG-9479, ADR-0074) — the manager's reader for the
 * `model_policy` block that ENG-9483 slice 1 put on the wire.
 *
 * Slice 1 made both `/host/exchange` and `/host/refresh` emit a resolved policy
 * and deliberately shipped no consumer, because the consumer is the half that
 * can stop a host spawning agents. This is that consumer, and it is written so
 * that every path a policy can take through it ends in one of two places:
 * "behave exactly as before" or "apply something we can fully honour". There is
 * no third branch where a policy is half-applied.
 *
 * ── Why a half-applied policy is the failure to design against ───────────────
 *
 * A gateway policy is a base URL AND a credential. Applying the base URL without
 * the credential points Claude Code at an endpoint it cannot authenticate
 * against, and the manager does not read that as an error: `persistent-session`
 * logs a missing credential as informational and spawns anyway (:1760), because
 * in gateway mode inference genuinely is supposed to come from elsewhere. The
 * result is a healthy-looking agent that cannot answer anything — the same shape
 * as the ENG-9481 channel failure, and just as silent.
 *
 * So the credential resolution is a SEAM, not an afterthought:
 * `resolveBindingCredential` is the single function that decides whether a
 * binding can be honoured. Today it returns null with a named reason for every
 * gateway source, because ENG-9481's stored-key-vs-pass-through question is
 * unanswered and that answer selects the shape of the credential
 * (`gateway_token` + `ANTHROPIC_CUSTOM_HEADERS`, or a plain `api_key`). When it
 * is answered, that one function changes and everything downstream — the env
 * injection at all three spawn sites, the egress allowlist, the auth tuple —
 * is already in place and already tested.
 *
 * ── On drift from the server's shape (ENG-8231) ─────────────────────────────
 *
 * The wire contract is declared in `packages/api/src/lib/model-policy.ts`, which
 * this package cannot import (the CLI depends on `@augmented/core`, not on the
 * API). That makes these two mirrored definitions of one contract, and CLAUDE.md
 * records what mirrored definitions do. Two things bound the damage:
 *
 *   1. `parseModelPolicy` is defensive by construction. A field the server
 *      renames reads as absent, which yields `null`, which means "no policy",
 *      which means every caller keeps its pre-policy behaviour. Drift degrades
 *      to inaction, never to a wrong action.
 *   2. `eng-9483-model-policy-wire-parity.test.ts` reads the API source and
 *      asserts the field names this parser looks for are the ones that file
 *      declares — so a rename fails CI here rather than going quiet in prod.
 */

/** Mirrors `ModelPolicyRole` in packages/api/src/lib/model-policy.ts. */
export type ModelPolicyRole = 'primary' | 'secondary';
/** Mirrors `ModelPolicyTransport`. */
export type ModelPolicyTransport = 'anthropic-direct' | 'bedrock' | 'gateway';
/** Mirrors `ModelPolicyWireSchema`. */
export type ModelPolicyWireSchema = 'anthropic_messages' | 'openai_chat_completions';
/** Mirrors `ModelPolicyCredentialSource`. */
export type ModelPolicyCredentialSource =
  | 'max_subscription'
  | 'api_key'
  | 'gateway_token'
  | 'aws_role';
/** Mirrors `ModelPolicyOrigin` — where in `agent ?? host ?? org` it was found. */
export type ModelPolicyOrigin = 'agent' | 'host' | 'organization';

/** One role's transport shape. Never carries a credential — the server does not send one. */
export interface ManagerModelPolicyBinding {
  readonly role: ModelPolicyRole;
  readonly transport: ModelPolicyTransport;
  readonly wireSchema: ModelPolicyWireSchema;
  /** Non-null iff transport is 'gateway' (server-side biconditional CHECK). */
  readonly baseUrl: string | null;
  readonly credentialSource: ModelPolicyCredentialSource;
}

/**
 * ENG-9816: one `(role, slot) -> model_id` entry of the policy's model map.
 *
 * `slot` is an open string on purpose — it is a name the AGENT'S FRAMEWORK owns
 * (`FRAMEWORK_MODEL_SLOTS`, packages/core/src/types/agent.ts), not a Claude
 * family and not a capability class. A slot this manager does not map to an env
 * var is not an error; it is a slot some other framework fills.
 */
export interface ManagerModelPolicyModel {
  readonly role: ModelPolicyRole;
  readonly slot: string;
  readonly modelId: string;
  /**
   * ENG-10866 — this model's context window in tokens, or null for "say nothing".
   *
   * Null for every Anthropic model, deliberately: the runtime already knows
   * those windows, and asserting one would make us a second writer of a number
   * it is authoritative about. Non-null is for the provider-prefixed gateway ids
   * its catalog does not contain, which it silently assumes to be 200k.
   */
  readonly contextWindow: number | null;
}

export interface ManagerModelPolicy {
  readonly policyId: string;
  readonly name: string;
  /** Bumped on every edit. This — not the whole object — is what gets fingerprinted. */
  readonly revision: number;
  readonly resolvedFrom: ModelPolicyOrigin;
  /**
   * ENG-9489 — which binding role this agent actually runs on.
   *
   * `'primary'` unless the control plane has promoted this host's policy to a
   * failover target, and `'primary'` for an API that predates the field. The
   * server guarantees a binding exists for it; `parseModelPolicy` re-checks,
   * because a role with no binding is a policy with no transport.
   */
  readonly activeRole: ModelPolicyRole;
  readonly bindings: readonly ManagerModelPolicyBinding[];
  /**
   * ENG-9816. Empty for an API that predates the map, for a policy that maps no
   * slots, and — deliberately — for a map this parser could not read in full.
   * See `parseModelPolicyModels` for why that last case degrades to empty rather
   * than dropping the policy the way a bad BINDING does.
   */
  readonly models: readonly ManagerModelPolicyModel[];
}

const TRANSPORTS: readonly string[] = ['anthropic-direct', 'bedrock', 'gateway'];
const WIRE_SCHEMAS: readonly string[] = ['anthropic_messages', 'openai_chat_completions'];
const CREDENTIAL_SOURCES: readonly string[] = [
  'max_subscription',
  'api_key',
  'gateway_token',
  'aws_role',
];
const ROLES: readonly string[] = ['primary', 'secondary'];

const isRecord = (v: unknown): v is Record<string, unknown> =>
  typeof v === 'object' && v !== null && !Array.isArray(v);

/**
 * ENG-9816 — parse the `(role, slot) -> model_id` map.
 *
 * ── Why a bad map yields an EMPTY map and not a dropped policy ───────────────
 *
 * A bad BINDING drops the whole policy, and rightly: a binding is transport plus
 * credential, and honouring half of one produces an agent that looks healthy and
 * can answer nothing. The model map is not that. Its absence means "the agent's
 * own `--model` alias governs", which is exactly the pre-ENG-9816 behaviour and
 * is safe by construction — so a malformed map that dropped the policy would
 * take the gateway config down with it and REGRESS the transport half that
 * ENG-9483 already ships.
 *
 * All-or-nothing WITHIN the map, though: one unreadable entry empties it rather
 * than applying the entries that happened to parse, because a policy that maps
 * `primary` and drops `small_fast` is a partially-applied policy — the exact
 * shape this file refuses everywhere else.
 *
 * An absent field is not malformed. Slice 1 of ENG-9483 shipped before this map
 * existed, and a manager that dropped a policy for a field an older API never
 * sent would fail closed against its own control plane.
 */
function parseModelPolicyModels(raw: unknown): ManagerModelPolicyModel[] {
  if (raw === undefined || raw === null) return [];
  if (!Array.isArray(raw)) return [];
  const out: ManagerModelPolicyModel[] = [];
  for (const entry of raw) {
    if (!isRecord(entry)) return [];
    const role = entry['role'];
    const slot = entry['slot'];
    const modelId = entry['model_id'];
    if (typeof role !== 'string' || !ROLES.includes(role)) return [];
    // Non-blank, mirroring `model_policy_models_slot_not_blank`. The legal SET
    // is deliberately not checked here: it is framework-owned (ENG-9485), and a
    // slot this manager cannot map is skipped at APPLY time, not rejected here.
    if (typeof slot !== 'string' || slot.trim() === '') return [];
    if (typeof modelId !== 'string' || modelId.trim() === '') return [];
    // ENG-10866. ABSENT and null both mean "no window", and both must be
    // accepted: the first is an API that predates the field, the second is a
    // model whose window the runtime already knows. Anything else present is
    // malformed and empties the map under the all-or-nothing rule above —
    // applying a bad window is worse than applying none, because the agent
    // would run at a size nothing measured.
    const windowRaw = entry['context_window'];
    let contextWindow: number | null = null;
    if (windowRaw !== undefined && windowRaw !== null) {
      if (typeof windowRaw !== 'number' || !Number.isSafeInteger(windowRaw) || windowRaw <= 0) {
        return [];
      }
      contextWindow = windowRaw;
    }
    out.push({
      role: role as ModelPolicyRole,
      slot: slot.trim(),
      modelId: modelId.trim(),
      contextWindow,
    });
  }
  return out;
}

function parseBinding(raw: unknown): ManagerModelPolicyBinding | null {
  if (!isRecord(raw)) return null;
  const role = raw['role'];
  const transport = raw['transport'];
  const wireSchema = raw['wire_schema'];
  const credentialSource = raw['credential_source'];
  if (typeof role !== 'string' || !ROLES.includes(role)) return null;
  if (typeof transport !== 'string' || !TRANSPORTS.includes(transport)) return null;
  if (typeof wireSchema !== 'string' || !WIRE_SCHEMAS.includes(wireSchema)) return null;
  if (typeof credentialSource !== 'string' || !CREDENTIAL_SOURCES.includes(credentialSource)) {
    return null;
  }
  const baseUrlRaw = raw['base_url'];
  const baseUrl = typeof baseUrlRaw === 'string' && baseUrlRaw.trim() !== '' ? baseUrlRaw : null;
  // The server enforces `transport = 'gateway' <-> base_url IS NOT NULL` with a
  // CHECK. Re-assert it rather than trust it: a gateway binding with no endpoint
  // has nothing to point at, and a non-gateway binding carrying one would be a
  // base URL nobody meant to apply. Either way the safe answer is to reject the
  // BINDING (which drops the policy below), not to invent a value for it.
  if ((transport === 'gateway') !== (baseUrl !== null)) return null;
  return {
    role: role as ModelPolicyRole,
    transport: transport as ModelPolicyTransport,
    wireSchema: wireSchema as ModelPolicyWireSchema,
    baseUrl,
    credentialSource: credentialSource as ModelPolicyCredentialSource,
  };
}

/**
 * Parse `refreshData.model_policy` / the exchange response's `model_policy`.
 *
 * Returns null — meaning "no policy, behave exactly as before" — for an absent
 * field, an older API that never sends one, a malformed block, or a policy whose
 * bindings do not all parse. That last one is deliberate and is the only choice
 * that is safe in both directions: a policy is a coherent unit (ADR-0074 §2),
 * so honouring the bindings that happened to parse would run an agent on a
 * combination nobody configured.
 *
 * Never throws. This sits on the manager's poll path.
 */
export function parseModelPolicy(raw: unknown): ManagerModelPolicy | null {
  if (!isRecord(raw)) return null;
  const policyId = raw['policy_id'];
  const name = raw['name'];
  const revision = raw['revision'];
  const resolvedFrom = raw['resolved_from'];
  if (typeof policyId !== 'string' || policyId === '') return null;
  if (typeof name !== 'string') return null;
  if (typeof revision !== 'number' || !Number.isFinite(revision)) return null;
  if (
    typeof resolvedFrom !== 'string' ||
    !['agent', 'host', 'organization'].includes(resolvedFrom)
  ) {
    return null;
  }
  const bindingsRaw = raw['bindings'];
  if (!Array.isArray(bindingsRaw) || bindingsRaw.length === 0) return null;
  const bindings: ManagerModelPolicyBinding[] = [];
  for (const b of bindingsRaw) {
    const parsed = parseBinding(b);
    if (!parsed) return null;
    bindings.push(parsed);
  }
  // ENG-9489. Three cases, and they are deliberately NOT the same:
  //   absent          -> 'primary'. An API older than ENG-9489 never sends it,
  //                      and a manager that dropped the policy over a field its
  //                      own control plane does not know about would fail closed
  //                      against itself — the rule `parseModelPolicyModels`
  //                      already states.
  //   not a legal role-> drop the policy. A value we cannot read is not the same
  //                      as one that was never sent: the server is telling us
  //                      which transport to run on and we cannot tell which.
  //   role with no    -> drop the policy. A binding IS the transport plus the
  //   binding            credential; naming a role we have neither for is the
  //                      half-applied policy this file refuses everywhere.
  const activeRoleRaw = raw['active_role'];
  let activeRole: ModelPolicyRole = 'primary';
  if (activeRoleRaw !== undefined && activeRoleRaw !== null) {
    if (typeof activeRoleRaw !== 'string' || !ROLES.includes(activeRoleRaw)) return null;
    activeRole = activeRoleRaw as ModelPolicyRole;
  }
  if (!bindings.some((b) => b.role === activeRole)) return null;
  return {
    policyId,
    name,
    revision,
    resolvedFrom: resolvedFrom as ModelPolicyOrigin,
    activeRole,
    bindings,
    models: parseModelPolicyModels(raw['models']),
  };
}

/**
 * The non-secret component this policy contributes to the manager's auth tuple.
 *
 * `revision` and not a hash of the object: the server bumps `revision` on every
 * edit and only on an edit, whereas diffing the object would respawn every agent
 * on the host for an `updated_at` touch that changed nothing an agent can see.
 * The policy id rides along so a REASSIGNMENT (agent moved to a different policy
 * that happens to sit at the same revision) is also a change — without it, two
 * distinct policies both at revision 1 are indistinguishable.
 *
 * Null when there is no policy, so an unpoliced fleet's tuple is byte-for-byte
 * what it is today and nothing respawns on deploy.
 *
 * ── ENG-9489: the active role rides along, and ONLY when it is not primary ───
 *
 * A failover changes which transport, credential and model an agent runs on
 * while `policyId` and `revision` both stay put — so without this the promotion
 * would be invisible to the tuple and the agent would keep running on its
 * primary until some unrelated change happened to respawn it. That is the whole
 * mechanism failing silently, which is the failure mode this epic exists to
 * remove.
 *
 * Suffixed only for a non-primary role, deliberately, for the same reason the
 * whole component is omitted when there is no policy: an agent on its primary
 * keeps the tuple it already has, so deploying this respawns nothing. Both
 * directions still work — promoting adds the suffix, reverting removes it, and
 * either way the string differs from the one the session launched with.
 *
 * `parseAuthTuple` takes everything after `|policy=` as one opaque value, so
 * this needs no parser change and the ENG-8293 diff line reads
 * `model_policy: p@5 -> p@5/secondary`, which names exactly what moved.
 */
export function modelPolicyAuthComponent(policy: ManagerModelPolicy | null): string | null {
  if (!policy) return null;
  const base = `${policy.policyId}@${policy.revision}`;
  return policy.activeRole === 'primary' ? base : `${base}/${policy.activeRole}`;
}

/**
 * Domains a policy needs reachable, in squid `dstdomain` form, for
 * `buildEgressAllowlist`.
 *
 * EVERY binding, not just the primary — a secondary that is unreachable fails at
 * the moment the primary degrades, which is the worst time to discover it, and
 * `EGRESS_BASELINE_DOMAINS` contains no gateway host at all so the default is
 * "unreachable" (surfacing as `connection_error`, ENG-9483's own description).
 *
 * This is deliberately NOT gated on `resolveBindingCredential`. An allowlist
 * entry is a PERMISSION, not an actuation: allowing a host that is never
 * contacted costs nothing, whereas withholding it until the credential path
 * lands would make the credential change break egress as a side effect.
 *
 * Bare hostname only (no leading dot): a policy names one endpoint, so widening
 * it to every subdomain would grant more than the operator configured.
 * `buildEgressAllowlist` validates the result against its own domain regex,
 * which rejects schemes, paths and whitespace.
 *
 * KNOWN GAP, recorded rather than silently inherited: that regex ACCEPTS an IPv4
 * literal (digits are in its character class), while the allowlist is enforced
 * through squid `dstdomain`, which matches names and not addresses. So a policy
 * whose base URL addresses a self-hosted gateway by IP receives an allowlist
 * entry that cannot match, and the agent fails with `connection_error` — the
 * same symptom as having no entry at all. The fix is a squid `dst` ACL beside
 * `dstdomain`, which is a change to the egress sidecar and not to this slice;
 * tightening the regex instead would only convert a silent no-op into a silent
 * omission, and it is shared with TOOLS.md entries that have behaved this way
 * since ENG-6579. A hostname-addressed gateway is unaffected.
 */
export function modelPolicyEgressDomains(policy: ManagerModelPolicy | null): string[] {
  if (!policy) return [];
  const out = new Set<string>();
  for (const b of policy.bindings) {
    if (!b.baseUrl) continue;
    try {
      const host = new URL(b.baseUrl).hostname.trim().toLowerCase();
      if (host) out.add(host);
    } catch {
      // A base_url that will not parse cannot be turned into an allowlist entry.
      // Skipping is right: it is the same outcome as having no policy, and the
      // binding itself is already unusable.
    }
  }
  return [...out];
}

/** Why a binding cannot be honoured yet. A value here is never a crash — it is a log line. */
export type BindingCredentialBlockedReason =
  | 'credential-path-not-built'
  | 'bedrock-not-implemented';

export type BindingCredentialResolution =
  /** Nothing to inject; the host's existing auth already serves this binding. */
  | { readonly kind: 'host-default' }
  /** The binding needs plumbing this manager does not have. Keep pre-policy behaviour. */
  | { readonly kind: 'blocked'; readonly reason: BindingCredentialBlockedReason }
  /** The binding is fully honourable. Only this arm may change the spawn env. */
  | {
      readonly kind: 'gateway';
      readonly baseUrl: string;
      readonly authToken: string;
    };

/**
 * THE ENG-9481 SEAM. The single place that decides whether a binding can be
 * honoured, and the only thing that has to change when ENG-9481 answers.
 *
 * `availableGatewayToken` is the credential the caller was able to obtain for
 * this binding. Production passes null today for every gateway source, because
 * no credential reaches the host: slice 1's resolver never selects
 * `model_policy_bindings.credential`, and it must not start doing so before
 * ENG-9481 settles stored-key vs pass-through — that answer decides whether the
 * shape is `gateway_token` (which also needs `ANTHROPIC_CUSTOM_HEADERS`, an
 * at-rest secret that does not exist in this tree) or a plain `api_key` (which
 * does not). Guessing wrong is not a refactor; it is a secret with the wrong
 * storage and reader-context requirements.
 *
 * Tests pass a token to exercise the honoured path end to end, which is why the
 * credential is a PARAMETER and not read in here — the plumbing below it is
 * proven now, not when the seam closes.
 *
 * ── One vendor's answer, recorded while adding their preset ─────────────────
 *
 * Vercel AI Gateway documents BOTH shapes, and which one applies turns on how
 * inference is paid for rather than on anything about the gateway:
 *
 *   - Their own API key   -> `ANTHROPIC_BASE_URL` + `ANTHROPIC_AUTH_TOKEN`.
 *     No custom headers. This is exactly what the gateway arm above already
 *     emits, so for THIS shape the plumbing is not the open question.
 *   - A Claude subscription routed through them -> `ANTHROPIC_CUSTOM_HEADERS`
 *     carrying `x-ai-gateway-api-key`. That pair is `max_subscription` +
 *     `gateway`, which SOURCE_TRANSPORT_MATRIX does not admit — and it buys
 *     observability over a subscription rather than a way past its limits, so
 *     it is not what a FALLBACK wants anyway.
 *
 * One vendor is not the answer to ENG-9481, which is a custody question — may
 * the platform hold a customer's gateway key and hand it to a host — not a
 * plumbing one. But it is a data point that the plumbing question has a
 * mundane answer for at least the failover case.
 *
 * ── The trap whoever closes this seam must not miss ─────────────────────────
 *
 * Claude Code reads `ANTHROPIC_API_KEY` FIRST, and a non-empty value there
 * beats `ANTHROPIC_AUTH_TOKEN` (Vercel's docs call this out twice). So a
 * gateway resolution must EMPTY that variable in the spawn env, not merely
 * decline to set it: `sourceSetsAnthropicApiKey` returning false says this code
 * does not add one, which is not the same as the host's base environment
 * lacking one. On an api_key-mode host, a gateway binding that only declines
 * would route straight past the gateway to Anthropic on the host's old key —
 * healthy agent, correct-looking spawn, wrong provider, no error anywhere.
 */
export function resolveBindingCredential(
  binding: ManagerModelPolicyBinding,
  availableGatewayToken: string | null,
): BindingCredentialResolution {
  switch (binding.transport) {
    case 'anthropic-direct':
      // Whatever the host is already configured with serves this: the policy is
      // describing the status quo. Note this is true for `api_key` too — a
      // direct-to-Anthropic api_key binding is the host's existing api_key mode,
      // which the manager already handles without any policy involvement.
      return { kind: 'host-default' };
    case 'bedrock':
      // ADR-0074 §2a resolves `aws_role` through the AWS credential chain, which
      // needs CLAUDE_CODE_USE_BEDROCK and a region — neither is threaded here.
      // Named separately from the gateway block so a Bedrock policy is not
      // misread as waiting on ENG-9481, which it is not.
      return { kind: 'blocked', reason: 'bedrock-not-implemented' };
    case 'gateway': {
      if (!binding.baseUrl) return { kind: 'blocked', reason: 'credential-path-not-built' };
      if (!availableGatewayToken) {
        return { kind: 'blocked', reason: 'credential-path-not-built' };
      }
      return { kind: 'gateway', baseUrl: binding.baseUrl, authToken: availableGatewayToken };
    }
  }
}

/**
 * The binding that governs inference — the one for the policy's ACTIVE role.
 *
 * ENG-9489. This read `role === 'primary'` until failover existed. It is not a
 * generalisation for its own sake: `parseModelPolicy` refuses a policy whose
 * active role has no binding, so the null here still means only what it always
 * meant — no policy at all.
 */
export function activeBinding(
  policy: ManagerModelPolicy | null,
): ManagerModelPolicyBinding | null {
  if (!policy) return null;
  return policy.bindings.find((b) => b.role === policy.activeRole) ?? null;
}

/**
 * Per ADR-0074 §2a, does this credential source put `ANTHROPIC_API_KEY` into the
 * spawn environment?
 *
 * An exhaustive switch and not a constant, deliberately. The answer is currently
 * `false` for all four sources, and writing that as `return false` would make it
 * a CLAIM — a fifth source added to the union would inherit it silently, which is
 * precisely how the rule this replaces went wrong (it kept keying off an enum
 * member whose meaning had changed underneath it). As a switch with no default,
 * widening `ModelPolicyCredentialSource` is a compile error until someone states
 * the new member's answer.
 */
function sourceSetsAnthropicApiKey(source: ModelPolicyCredentialSource): boolean {
  switch (source) {
    case 'max_subscription':
      // Nothing in env at all — inference uses the host's /root/.claude OAuth.
      return false;
    case 'api_key':
      // ANTHROPIC_AUTH_TOKEN (+ ANTHROPIC_BASE_URL when gateway). Note this is
      // the SAME wire shape as OpenRouter, which the purge already exempts.
      return false;
    case 'gateway_token':
      // ANTHROPIC_AUTH_TOKEN + ANTHROPIC_BASE_URL.
      return false;
    case 'aws_role':
      // The AWS credential chain, for `bedrock`.
      return false;
  }
}

/**
 * Does `ANTHROPIC_API_KEY` end up in this agent's spawn environment?
 *
 * This is the predicate the OAuth purge in `resolveOAuthCredAction` was always
 * protecting against — a stale claude.ai OAuth session shadowing
 * `ANTHROPIC_API_KEY`, whose precedence is version-dependent and undocumented.
 * It is asked here as the question rather than as an enum membership test,
 * because the enum member changed meaning underneath it: ADR-0074's `api_key`
 * credential source resolves to `ANTHROPIC_AUTH_TOKEN`, the same shape as
 * OpenRouter, which is already exempt from the purge for exactly that reason.
 *
 * With a policy in play the answer is false for every source in ADR-0074 §2a, so
 * an org running gateway inference plus a host subscription for channels keeps
 * its OAuth creds — the configuration ENG-9481 §1 describes, whose failure mode
 * is silent (healthy agent, correct billing, nobody can reach it).
 *
 * Null policy returns false and the caller must NOT read that as "do not purge":
 * with no policy the pre-policy rule still governs, and this function has no
 * opinion. See `resolveOAuthCredAction`, which branches on the policy first.
 */
export function policyPutsAnthropicApiKeyInEnv(policy: ManagerModelPolicy | null): boolean {
  if (!policy) return false;
  return policy.bindings.some((b) => sourceSetsAnthropicApiKey(b.credentialSource));
}

/**
 * ENG-9816 — which env var each claude-code slot is delivered through.
 *
 * These two names are the launcher's contract with Claude Code, which is why the
 * map lives here and not in `FRAMEWORK_MODEL_SLOTS`: core declares WHICH slots a
 * framework has, this declares HOW this manager delivers them. The keys are
 * asserted equal to `modelSlotsForFramework('claude-code')` in the ENG-9816
 * tests — a slot core gains and this does not map would otherwise be dropped in
 * silence, which is the ENG-8231 allowlist-drift shape.
 *
 * A slot absent from this map is skipped, not an error: `opencode` names its
 * models `provider/model` and never reads these vars at all.
 *
 * A Map, not an object literal, because `slot` is an UNVALIDATED open string off
 * the wire (CodeRabbit, PR #5366). An object literal inherits from
 * `Object.prototype`, so a slot named `constructor` / `toString` / `valueOf` /
 * `hasOwnProperty` resolves to a truthy non-string that the `if (!key)` guard
 * below happily passes — producing a garbage env var name AND, worse, making
 * `policyModelActive` true, which suppresses the `--model` argv for a slot that
 * has no actuator at all. The agent would then run with no model selection from
 * either source. A Map has no prototype chain to fall through, so the class is
 * removed rather than guarded; `hasOwnProperty.call` would also work, but only
 * for as long as everyone remembers to write it.
 */
const CLAUDE_CODE_SLOT_ENV: ReadonlyMap<string, string> = new Map([
  ['primary', 'ANTHROPIC_MODEL'],
  ['small_fast', 'ANTHROPIC_SMALL_FAST_MODEL'],
]);

/**
 * HOW each claude-code slot reaches the runtime. Every slot core declares must
 * appear here, in both directions — that is the ENG-8231 drift assertion, and
 * `eng-9816-model-policy-slot-map.test.ts` enforces it.
 *
 * ENG-9816 asserted something narrower: that every declared slot had an ENV
 * actuator. That held while every slot was one, and its comment named ENG-9811
 * as the change that would break it. It did. `advisor` is not an env var —
 * there is no `ANTHROPIC_ADVISOR_MODEL` — so the honest repair is to widen the
 * invariant from "has an env actuator" to "has a STATED actuator", rather than
 * to relax the count and lose the drift catch entirely.
 *
 * `'project-settings'` means the value is delivered by writing `advisorModel`
 * into `<projectDir>/.claude/settings.json`, which the MANAGER writes at spawn
 * (`writeProjectClaudeSettings`, project-claude-settings.ts). That path is
 * MEASURED to work and to override user-level settings (CLI 2.1.261 —
 * docs/research/eng-9811-advisor-model-delivery.md), which is what makes it
 * agent-grain rather than a repeat of `hosts.claude_auth_mode`.
 *
 * ENG-10075 CORRECTION — READ THIS BEFORE TRUSTING ANY NEARBY SENTENCE ABOUT
 * "the settings.json the adapter generates". The claudecode adapter DOES emit a
 * `settings.json` artefact, and Claude Code does not read it: the manager
 * materialises artefacts at `join(agentDir, relativePath)`, so it lands at
 * `<agentDir>/settings.json`, while the runtime reads `~/.claude/settings.json`
 * and `<projectDir>/.claude/settings.json`. ENG-5631 recorded this already, in
 * claudecode/index.ts, about its own inert `model` field. The earlier wording
 * here named the adapter's file, and building to it would have written
 * `advisorModel` somewhere nothing reads — a policy row set, a file written, a
 * drift tracker satisfied, and no advisor. Exactly the silent-attach failure
 * ENG-9811 §5 exists to prevent, rebuilt inside its own fix.
 *
 * `advisor` is STILL not in `CLAUDE_CODE_SLOT_ENV` above, and must not be: it
 * contributes no env var, so it cannot set `policyModelActive` and cannot
 * suppress the `--model` argv (the ENG-9747 shape).
 */
export const CLAUDE_CODE_SLOT_DELIVERY: ReadonlyMap<string, 'env' | 'project-settings'> = new Map([
  ['primary', 'env'],
  ['small_fast', 'env'],
  ['advisor', 'project-settings'],
]);

/**
 * ENG-10075 — the settings.json KEY each `project-settings` slot writes.
 *
 * A Map for the same reason `CLAUDE_CODE_SLOT_ENV` is one: `slot` is an
 * unvalidated open string off the wire, and an object literal would resolve
 * `constructor` / `toString` to an inherited truthy non-string that the falsy
 * guard below waves through — here that would mean writing a garbage key into a
 * file Claude Code parses at startup.
 *
 * Every entry must be a key whose ABSENCE is the off state, because that is what
 * makes the removal path honest: when a policy stops naming an advisor, the
 * writer deletes the key rather than writing a sentinel, and Claude Code returns
 * to its own default. A key needing an explicit "off" value would need a second
 * concept here and does not exist yet.
 */
const CLAUDE_CODE_SLOT_SETTINGS_KEY: ReadonlyMap<string, string> = new Map([
  ['advisor', 'advisorModel'],
]);

/** The settings.json keys this manager OWNS — everything else in the file is the operator's. */
export const MANAGED_PROJECT_SETTINGS_KEYS: readonly string[] = [
  ...CLAUDE_CODE_SLOT_SETTINGS_KEY.values(),
];

/**
 * The project-settings fragment this policy contributes for one role.
 *
 * The `project-settings` sibling of `modelEnvForRole`, and deliberately its
 * mirror image: same role keying, same "a slot with no actuator here is not an
 * error" rule, same last-write-wins note. Splitting the two would have let the
 * env half and the file half disagree about which role is active — a policy
 * half-applied, which is the one outcome this file has no branch for.
 */
export function projectSettingsForRole(
  policy: ManagerModelPolicy | null,
  role: ModelPolicyRole,
): Record<string, string> {
  if (!policy) return {};
  const out: Record<string, string> = {};
  for (const m of policy.models) {
    if (m.role !== role) continue;
    const key = CLAUDE_CODE_SLOT_SETTINGS_KEY.get(m.slot);
    if (!key) continue;
    out[key] = m.modelId;
  }
  return out;
}

/**
 * The model env this policy contributes for one role.
 *
 * Keyed on ROLE because a policy's map spans both — the rows for the role an
 * agent is NOT running on describe a configuration it never reaches, and must
 * not leak into its env. Callers pass the policy's ACTIVE role, which ENG-9489
 * made something other than `primary`.
 *
 * Last write wins on a duplicate `(role, slot)`, which the server's PK makes
 * unreachable. Stated rather than defended against: inventing a tie-break for a
 * state the database forbids would be a rule nobody could ever read the effect of.
 */
export function modelEnvForRole(
  policy: ManagerModelPolicy | null,
  role: ModelPolicyRole,
): Record<string, string> {
  if (!policy) return {};
  const out: Record<string, string> = {};
  for (const m of policy.models) {
    if (m.role !== role) continue;
    const key = CLAUDE_CODE_SLOT_ENV.get(m.slot);
    // Not an error. A slot this framework does not deliver through an env var —
    // or one belonging to another framework entirely — simply has no actuator
    // here, and the agent's pre-policy default keeps governing it.
    if (!key) continue;
    out[key] = m.modelId;
  }
  return out;
}

/**
 * ENG-10866 — the variable that tells Claude Code how big the model's context is.
 *
 * Named once because two files have to agree on it: this module injects it, and
 * `persistent-session.ts` forwards it into a container BY NAME. A var injected
 * and not forwarded is invisible on a bare host and fails only on an isolated
 * one — which is the direction the fleet is moving.
 */
export const CLAUDE_CODE_CONTEXT_WINDOW_ENV = 'CLAUDE_CODE_MAX_CONTEXT_TOKENS';

/**
 * The context-window fragment this policy contributes for one role.
 *
 * ── Why this is not a third entry in `CLAUDE_CODE_SLOT_ENV` ─────────────────
 *
 * That map answers "which variable carries the model for this slot", one slot
 * to one variable. A window is not a slot — it is a SECOND fact about the model
 * a slot already named, and there is exactly one variable for it however many
 * slots a policy fills. Adding it there would have made `modelEnvForRole` emit
 * a value that is not a model id, which line 981's consumer reads as one.
 *
 * ── Why the primary slot, and only it ───────────────────────────────────────
 *
 * `CLAUDE_CODE_MAX_CONTEXT_TOKENS` is process-wide: there is no per-slot
 * variant. The window that governs is therefore the one the main loop accounts
 * against — the model in the slot that fills `ANTHROPIC_MODEL` — because that
 * is the loop auto-compaction truncates.
 *
 * That last clause is MEASURED, not inferred (2026-09-22, CLI 2.1.278). Forcing
 * compaction with `CLAUDE_CODE_MAX_CONTEXT_TOKENS=30000` emits
 * `[claude-code:unrecognized_model] {"model":"…","query_source":"compact"}`
 * naming the PRIMARY model, never the small/fast one — so the thing that
 * handles a large context is the model whose window this variable describes.
 *
 * The same run settles the neighbouring worry, which is the one an operator
 * actually asks: does a SMALL-window fast model get overrun when a large window
 * is declared? On this path it cannot. An `ANTHROPIC_SMALL_FAST_MODEL` set to a
 * deliberately non-existent id produced output byte-identical to a valid one
 * across five turns including four compactions — the fast model is not called
 * at all. So pairing a 1M-window primary with a 128k fast model is not a
 * hazard, and nothing here needs to compare the two.
 *
 * This comment previously justified that with "small_fast models routinely
 * declare a different window (65k to 500k)". THAT WAS WRONG, and wrong in a way
 * worth leaving a mark on: 65,536 is `google/gemini-2.5-flash-lite`'s
 * `max_tokens` — its OUTPUT cap — read off the column beside the one that
 * matters. The context windows actually measured on 2026-09-22 were 500,000 to
 * 1,050,000, a narrow band with no small-window model in it. The design
 * conclusion above does not depend on the spread and is unchanged; the number
 * did not support it and is gone.
 *
 * What the real 65,536-to-500,000 spread describes is `max_tokens`, which is a
 * different axis with its own consequence: it is why a failover swap changes
 * the spend profile (ENG-10868), not why a window is per-slot.
 *
 * The gate is written against the slot's ENV ACTUATOR rather than the literal
 * name `'primary'`, which is what makes the pairing structural: THIS FUNCTION
 * cannot put a window into the map unless `ANTHROPIC_MODEL` is in it too, so a
 * window is never delivered for a model the agent is not running.
 *
 * ── The broader claim is FALSE, and worth saying so here ────────────────────
 *
 * This comment previously read "`modelEnv` can never be non-empty without
 * `ANTHROPIC_MODEL` in it", and offered that as the reason `policyModelActive`
 * is safe to read as the map's emptiness. It is not true: a policy naming only
 * `small_fast` yields `{ANTHROPIC_SMALL_FAST_MODEL: …}` — non-empty, no
 * primary. That state is named, reachable and deliberate. The API calls it
 * `applied-without-primary` (`packages/api/src/lib/model-policy-actuation.ts`)
 * precisely because it suppresses the `--model` argv while setting no primary
 * model, leaving Claude Code's own built-in default to win.
 *
 * So `policyModelActive` does NOT imply `ANTHROPIC_MODEL` is set, and a reader
 * who took the old sentence at face value would have concluded exactly the
 * thing `applied-without-primary` exists to warn about. Found by obi-wan on
 * PR #6386.
 *
 * What this function owes is the narrow invariant above, and only that. Both
 * are pinned in eng-10866-context-window.test.ts — the narrow one as the rule,
 * the broad one as a case recorded to stay false.
 */
export function contextWindowEnvForRole(
  policy: ManagerModelPolicy | null,
  role: ModelPolicyRole,
): Record<string, string> {
  if (!policy) return {};
  for (const m of policy.models) {
    if (m.role !== role) continue;
    if (CLAUDE_CODE_SLOT_ENV.get(m.slot) !== 'ANTHROPIC_MODEL') continue;
    // Null is the ordinary case and means "say nothing", which leaves the
    // runtime's own catalog governing. It is NOT a zero and must never become
    // one: an explicit 0 would be a window, and an impossible one.
    if (m.contextWindow === null) continue;
    return { [CLAUDE_CODE_CONTEXT_WINDOW_ENV]: String(m.contextWindow) };
  }
  return {};
}

/** What a policy contributes to an agent's spawn environment, plus what to say about it. */
export interface ModelPolicySpawnEnv {
  /**
   * Variables to inject. EMPTY unless the primary binding was fully honourable —
   * there is no partial application, because a base URL without a credential
   * produces an agent that looks healthy and cannot answer anything (the
   * missing-credential path in persistent-session is informational by design,
   * since in gateway mode inference is supposed to come from elsewhere).
   */
  readonly env: Record<string, string>;
  /**
   * ENG-9816 — the model selection, kept SEPARATE from `env` on purpose.
   *
   * `env` answers "does the policy govern this agent's endpoint and credential",
   * and the caller's `policyEnvActive` reads it as exactly that: it sits in an
   * if/else chain where a truthy value SUPPRESSES the host's `api_key` branch.
   * A model is orthogonal to a credential — an `api_key` host running a policy
   * that only names a model still needs its `ANTHROPIC_API_KEY` — so folding
   * these two together would silently strip that host's credential the moment an
   * operator picked a model. Merged into the spawn env by the caller, outside
   * that chain.
   *
   * Empty in OpenRouter mode (which owns `ANTHROPIC_MODEL` already) and for a
   * blocked binding (no partial application: if the endpoint is not honoured,
   * the model that was chosen for it must not be either).
   */
  readonly modelEnv: Record<string, string>;
  /**
   * ENG-10075 — the fragment to merge into `<projectDir>/.claude/settings.json`.
   *
   * A THIRD field rather than a flag on `modelEnv`, because it is delivered by a
   * different mechanism (a file the runtime reads at startup, not a variable in
   * the spawn env) and the caller has to act on it differently. Folding it into
   * `modelEnv` would put a settings key into a `-e` push.
   *
   * It is computed in the SAME switch as `modelEnv` so it inherits every branch's
   * semantics for free — empty in OpenRouter mode, empty for a blocked binding.
   * That is the whole reason it lives here instead of being a second call the
   * spawner makes on its own: a separately-computed advisor could survive a
   * branch that suppressed the model, which is a policy half-applied.
   */
  readonly projectSettings: Record<string, string>;
  /**
   * A line for manager.log, or null when there is nothing worth saying.
   *
   * Null for the two ordinary cases — no policy at all, and a policy that merely
   * describes what the host already does — because a line on every poll of a
   * correctly configured fleet is a line nobody reads. Non-null whenever a policy
   * exists but did NOT take effect, which is the state an operator would
   * otherwise have to infer from an agent's silence.
   */
  readonly notice: string | null;
}

/**
 * Decide what a model policy contributes to one agent's spawn env.
 *
 * Extracted from `spawnSession` rather than written inline because `spawnSession`
 * cannot be unit-called without a host — the precedent in this package is to fall
 * back to asserting on source text (see eng-8800-tmux-agent-id.test.ts, which
 * says so in its own header). Source assertions cannot tell an applied policy
 * from a blocked one, and that distinction is the entire safety property here, so
 * the decision lives in a pure function and only the two `-e` pushes that consume
 * its result are left to source-level checks.
 */
export function resolveModelPolicySpawnEnv(input: {
  readonly policy: ManagerModelPolicy | null;
  /** True when the legacy ENG-7152 OpenRouter path governs this agent. */
  readonly openRouterMode: boolean;
  /** The ENG-9481-gated credential. Production passes null; tests pass a value. */
  readonly gatewayToken: string | null;
  readonly codeName: string;
}): ModelPolicySpawnEnv {
  const { policy, openRouterMode, gatewayToken, codeName } = input;
  const active = activeBinding(policy);
  if (!policy || !active) return { env: {}, modelEnv: {}, projectSettings: {}, notice: null };
  // ENG-9489: named once so every branch below says the same thing about the
  // same fact. Empty on the primary so a fleet that has never failed over reads
  // exactly the log lines it read before.
  const roleNote = policy.activeRole === 'primary' ? '' : ` role=${policy.activeRole} (FAILOVER)`;

  if (openRouterMode) {
    // ENG-9483 calls this out as "two actuators, not one": OpenRouter rides its
    // own model hot-reload restart path, and unifying them is a behavioural
    // migration on live agents, not part of this slice. OpenRouter keeps
    // precedence — but a host in both states at once is a configuration nobody
    // intended, and silence would make the policy look applied.
    return {
      env: {},
      // ENG-9816: no model either. OpenRouter sets `ANTHROPIC_MODEL` from its own
      // config a few lines below in the caller, and two writers of one variable
      // is how the "two actuators" problem above becomes a wrong model rather
      // than a logged conflict.
      modelEnv: {},
      // ENG-10075: and no advisor. OpenRouter wins the whole policy here, not
      // the env half of it — writing an advisor into the settings file for an
      // agent whose model selection this branch just refused would be the
      // half-applied state the `blocked` branch below spells out.
      projectSettings: {},
      notice: `[model-policy] '${codeName}' has policy '${policy.name}' but is in OpenRouter mode — OpenRouter wins, policy not applied (ENG-9483: unifying the two actuators is a separate migration)`,
    };
  }

  // ENG-9816: the map is read for the ACTIVE binding's role. The other role's
  // rows describe a configuration this agent is not running on, so leaking them
  // into its env would be a policy half-applied. ENG-9489 is what makes the
  // active role something other than `primary`.
  // ENG-10866: the window rides WITH the model, in the same map, resolved for
  // the same role. Merged here rather than returned as a fourth field because
  // it is delivered by the same mechanism (a `-e` push) and must inherit every
  // branch below — a window applied under a blocked binding would size an agent
  // for a model it was refused.
  const modelEnv = {
    ...modelEnvForRole(policy, active.role),
    ...contextWindowEnvForRole(policy, active.role),
  };
  // ENG-10075: same role, same map, same call site. See `projectSettings` on the
  // interface for why this is not resolved separately by the spawner.
  const projectSettings = projectSettingsForRole(policy, active.role);
  const modelNote =
    Object.keys(modelEnv).length > 0
      ? ` models=${Object.entries(modelEnv)
          .map(([k, v]) => `${k}=${v}`)
          .join(' ')}`
      : '';
  // Logged separately from `modelNote` because it is delivered separately: an
  // operator reading "models=..." and seeing no advisor should be able to tell
  // "the policy names none" from "the policy names one and it went somewhere
  // else". Empty string when there is none, so the existing lines are unchanged
  // for every agent that has no advisor row.
  const advisorNote =
    Object.keys(projectSettings).length > 0
      ? ` project-settings=${Object.entries(projectSettings)
          .map(([k, v]) => `${k}=${v}`)
          .join(' ')}`
      : '';

  const resolution = resolveBindingCredential(active, gatewayToken);
  switch (resolution.kind) {
    case 'gateway':
      return {
        env: {
          ANTHROPIC_BASE_URL: resolution.baseUrl,
          ANTHROPIC_AUTH_TOKEN: resolution.authToken,
        },
        modelEnv,
        projectSettings,
        notice: `[model-policy] '${codeName}' inference via gateway ${resolution.baseUrl} (policy '${policy.name}' rev ${policy.revision}, from ${policy.resolvedFrom})${roleNote}${modelNote}${advisorNote}`,
      };
    case 'blocked':
      return {
        env: {},
        // No partial application. The chosen model belongs to an endpoint this
        // manager could not honour, so applying it alone would run the agent on
        // the host's existing credential against a model the operator picked for
        // somewhere else — a policy half-applied, which is the one outcome this
        // file has no branch for.
        modelEnv: {},
        // ENG-10075: and no advisor, for the identical reason. An advisor is a
        // model choice made for an endpoint this manager could not honour.
        projectSettings: {},
        notice: `[model-policy] '${codeName}' policy '${policy.name}' NOT applied — reason=${resolution.reason}; keeping existing auth (transport=${active.transport}, credential_source=${active.credentialSource})${roleNote}`,
      };
    case 'host-default':
      // The policy describes what the host already does for CREDENTIALS — but it
      // may still select a model, and for the `anthropic-direct` +
      // `max_subscription` fleet this branch is the only one that ever runs. So
      // this is where a model map actually takes effect today, and the notice
      // stops being silent exactly when there is something to say.
      return {
        env: {},
        modelEnv,
        projectSettings,
        notice:
          modelNote || roleNote || advisorNote
            ? `[model-policy] '${codeName}' model from policy '${policy.name}' rev ${policy.revision} (from ${policy.resolvedFrom})${roleNote};${modelNote || ' no slot maps to an env var'}${advisorNote}`
            : null,
      };
  }
}

/**
 * ENG-10113 — what `failoverRestartNotice` decided, not just what it says.
 *
 * The caller labels the injected message with `task_name`, which reaches a
 * surface an operator reads. One function now produces two different events, so
 * the caller is handed the reason rather than being left to re-derive it from
 * the wording (fragile) or to file both under `model-policy-failover` (a small
 * lie about a restart that was not a failover, in exactly the kind of record
 * somebody later trusts).
 */
export interface ModelPolicyRestartNotice {
  readonly reason: 'failover' | 'model-change';
  readonly text: string;
}

/**
 * ENG-9489 — the courtesy notice to inject before a failover respawn, or null.
 *
 * ── Why this exists at all ──────────────────────────────────────────────────
 *
 * The hot-reload path for an agent's own model already tells the agent before it
 * restarts it ("Your model was changed to X… your manager will restart your
 * session shortly", manager-worker.ts). The auth-tuple respawn that a failover
 * rides injects NOTHING — it stops the session mid-conversation and the agent
 * comes back with no account of why. These agents have names and personas and
 * are talking to end users at the time; changing which provider and model they
 * are, without a word, is the one consequence of this whole epic that a person
 * outside the fleet actually experiences.
 *
 * ── Why it returns null for an ordinary revision bump ───────────────────────
 *
 * The `model_policy` tuple component moves whenever a policy is EDITED, which is
 * already common and already silent. Speaking on every edit would put a restart
 * announcement in front of users for a description change. So the trigger is
 * specifically a change of ROLE — a different transport, credential and model
 * map — and an edit that leaves the role alone stays as quiet as it is today.
 *
 * ── Why the role is read back out of the tuple ──────────────────────────────
 *
 * The manager holds the CURRENT policy but not the one the session launched
 * with; the recorded tuple is the only surviving evidence of the latter, which
 * is exactly what `parseAuthTuple` exists for. Reading `from` and `to` out of
 * the diff keeps this a pure function of two strings the caller already has,
 * rather than requiring a second source of history to be kept in sync.
 *
 * Pure, so the DECISION is unit-testable — `spawnSession` and the poll body
 * cannot be unit-called without a host, the same constraint that put
 * `resolveModelPolicySpawnEnv` in this file.
 */
export function failoverRestartNotice(input: {
  /** `AuthTupleDiff.components`, structurally — taken by shape so this file does not depend on that module. */
  readonly components: readonly { component: string; from: string; to: string }[];
  /** The policy as of this poll, for the model the agent is moving ONTO. */
  readonly policy: ManagerModelPolicy | null;
  /**
   * ENG-10113 — the policy-resolved model the RUNNING session was launched with,
   * as recorded by the manager. `undefined` means "not known", which is a real
   * and common state (see the silence rules below), not "no model".
   */
  readonly previousModel?: string | undefined;
  /** ENG-10113 — the policy-resolved model the next spawn will carry. */
  readonly currentModel?: string | undefined;
}): ModelPolicyRestartNotice | null {
  const entry = input.components.find((c) => c.component === 'model_policy');
  if (!entry) return null;
  const fromRole = roleFromAuthComponent(entry.from);
  const toRole = roleFromAuthComponent(entry.to);

  if (fromRole !== toRole) {
    // Name the model only when the policy actually delivers one for the role being
    // moved onto. It is frequently absent — a policy may map no slot at all, and a
    // blocked binding deliberately contributes nothing — and inventing a name for
    // that case would tell the agent it is on a model it is not on.
    //
    // ENG-10113: `currentModel`, NOT `modelEnvForRole`. The map says what the
    // policy CHOSE for this role; `currentModel` is what the spawn path will
    // actually apply (`policyResolvedModel` → `resolveModelPolicySpawnEnv`), and
    // they diverge on a blocked binding — which is every gateway binding today,
    // pending ENG-9481 — and in OpenRouter mode. Reading the map here would name
    // a model in the very promotion where none gets applied, i.e. exactly the
    // failure the sentence above says it is avoiding. Same defect this ticket
    // fixed for the recorded baseline; it was still standing one branch over.
    //
    // A caller that omits `currentModel` degrades to the unnamed form rather than
    // guessing, which is the safe direction: this function will never name a
    // model the agent is not on.
    const model = input.currentModel;
    const onto = model ? `${toRole} model provider (${model})` : `${toRole} model provider`;
    const direction =
      toRole === 'primary'
        ? `Your manager is switching you back to your ${onto}.`
        : `Your manager is switching you to your ${onto} — this policy has failed over.`;
    return {
      reason: 'failover',
      text:
        `${direction} Claude Code applies the provider and model at launch, ` +
        `so your session will restart shortly to make the switch.`,
    };
  }

  // ENG-10113 — the role held still, but the MODEL may not have. A policy change
  // that leaves the role alone and swaps the model is the same user-visible event
  // as a direct model change, which announces itself two thousand lines away in
  // `manager-worker.ts`; before this branch the policy path was the quieter of the
  // two for an identical consequence. Measured on `scout`, 2026-09-06: an
  // agent-scoped reassignment took ANTHROPIC_MODEL opus → sonnet and the session
  // was taken away mid-conversation without a word.
  //
  // The discriminator is the MODEL, deliberately, and not the policy identity. The
  // tempting rule — "speak when the policy id changes, stay silent on a revision
  // bump" — reads correctly off that incident and misses the commonest case there
  // is: an operator editing a policy's own model slot bumps `revision` on the same
  // id, so the tuple reads `<id>@5 → <id>@6`, byte-for-byte the shape of a
  // description edit. Nothing in the tuple separates them, which is why the caller
  // has to RECORD the model rather than derive it. `modelPolicyAuthComponent` must
  // keep encoding only `<policyId>@<revision>[/role]`: the byte-for-byte stability
  // of its primary form is what stops a deploy restarting the whole policed fleet.
  const { previousModel, currentModel } = input;

  // Both halves must be known. The two ways they are not are both deliberate:
  //
  //   - `previousModel` is undefined when a policy is newly ASSIGNED (the model
  //     the session launched with came from `--model`, never from a policy, so the
  //     manager never recorded one) and on the first poll after a manager restart
  //     (the map is in memory only — the same emptiness that made the hot-reload
  //     path classify a live change as first-provision and stay silent).
  //   - `currentModel` is undefined when a policy is UNASSIGNED, or resolves no
  //     model for its active role. The agent does revert to its `--model` default
  //     there, which is a real change, but naming it would mean resolving a model
  //     this function is not given; announcing one without a name ("your model
  //     changed to something") is worse than the silence it replaces. Recorded as
  //     a known bound rather than inherited quietly — widening it is a separate
  //     change that needs the fallback model threaded in.
  if (!previousModel || !currentModel || previousModel === currentModel) return null;

  return {
    reason: 'model-change',
    text:
      `Your model was changed to ${currentModel}. Claude Code applies the model at launch, ` +
      `so your session will restart shortly to make the switch.`,
  };
}

/**
 * ENG-10113 — the model a policy actually PUTS on an agent, or undefined.
 *
 * The manager records this per session so a later poll can tell a model-changing
 * policy edit from a cosmetic one, and announce the first before taking the
 * session away.
 *
 * ── Why it delegates instead of reading the map ─────────────────────────────
 *
 * The obvious implementation is `modelEnvForRole(policy, policy.activeRole)`, and
 * it is wrong in a way that produces a CONFIDENTLY WRONG notice rather than a
 * missing one. `modelEnvForRole` answers "what does the map say for this role";
 * `resolveModelPolicySpawnEnv` answers "what does the agent actually get", and it
 * withholds the model on FOUR conditions, not one:
 *
 *   1. no policy, or a policy whose active role has no binding;
 *   2. OpenRouter mode, which owns `ANTHROPIC_MODEL` itself — two writers of one
 *      variable is how ENG-9483's "two actuators" becomes a wrong model;
 *   3. a `blocked` credential resolution — no partial application, since a model
 *      chosen for an endpoint this manager could not honour must not be applied
 *      against the host's existing credential. Every gateway binding resolves
 *      here today, pending ENG-9481;
 *   4. a role whose map names no slot that rides an env var.
 *
 * Mirroring only condition 2 leaves 1, 3 and 4 recording a model the actuator
 * never applied — and it fails in BOTH directions. A later edit would announce a
 * change away from a model the agent was never running; and when a blocked
 * binding is unblocked, the recorded value already equals the newly-applied one,
 * so the poll at which the model genuinely starts taking effect is the one that
 * stays silent.
 *
 * So this is the same argument `projectSettings` makes on the interface above —
 * computed in the same switch so it inherits every branch's semantics for free —
 * applied to the recorded baseline. A separately-derived value could survive a
 * branch that suppressed the model, which is a policy half-applied.
 *
 * The notice string this discards is not logged here; the spawn path owns that.
 */
export function policyResolvedModel(input: {
  readonly policy: ManagerModelPolicy | null;
  readonly openRouterMode: boolean;
  /**
   * Must be the SAME value the launcher config carries for this agent — null in
   * production today, deliberately (see `modelPolicyGatewayToken` at the spawn
   * call site). When ENG-9481 answers stored-key-vs-pass-through and a real token
   * appears, both sites have to take it from one place, or a gateway binding
   * resolves `blocked` for one of them and not the other.
   */
  readonly gatewayToken: string | null;
  readonly codeName: string;
}): string | undefined {
  return resolveModelPolicySpawnEnv(input).modelEnv.ANTHROPIC_MODEL;
}

/**
 * The role encoded in a `model_policy` auth-tuple component.
 *
 * `<policyId>@<revision>` means primary — the suffix is omitted for it, so that
 * an agent on its primary carries the tuple it always did (see
 * `modelPolicyAuthComponent`). Anything after the last `/` is the role.
 *
 * A value this cannot read yields `'primary'`, matching the parser's rule for an
 * absent `active_role`. That matters for one real case: `parseAuthTuple` reports
 * a one-sided component with the sentinel `'none'`, so a policy being newly
 * ASSIGNED or UNASSIGNED lands here — and announcing a failover for it would be
 * a notice about something that did not happen.
 */
function roleFromAuthComponent(value: string): ModelPolicyRole {
  const at = value.lastIndexOf('/');
  if (at === -1) return 'primary';
  const role = value.slice(at + 1);
  return ROLES.includes(role) ? (role as ModelPolicyRole) : 'primary';
}
